import { normalizeString } from "../../utils/normalizeString/normalizeString";
import { Mail, MailViewContent } from "./types/mail";

export class MailRecord {
    // WS3 : Record multi-copies — Record<internetMessageId, Map<parentFolderId, Mail>>.
    // Le niveau interne est une Map (pas un objet) DÉLIBÉRÉMENT : sans signature d'index, tout accès
    // `record[imid].propMail` ou écriture `record[imid] = mail` devient une erreur tsc → couverture
    // exhaustive des sites à router via les helpers _getCanonicalMail / _getCopy / _setMailInRecord /
    // _allCopies / _findCopyByGraphId. Les opérations niveau message (delete record[imid], `imid in record`,
    // Object.keys) restent inchangées.
    public byInternetMessageId: Record<string, Map<string, Mail>> = {};

    public inboxFolderId?: string = undefined;

    public deleteditemsFolderId?: string;

    public sentitemsFolderId?: string;

    public archiveFolderId?: string;

    public junkemailFolderId?: string;

    // Guard pour éviter de re-fetcher les pièces jointes une fois qu'une COPIE a été enrichie.
    // Clé = uniqueMailvistaId (niveau COPIE, et NON internetMessageId) : un mail self-sent porte
    // deux copies (Inbox + Sent) sous le même internetMessageId, qui doivent pouvoir être enrichies
    // indépendamment. Une copie n'est ajoutée qu'APRÈS un enrichissement ABOUTI (PJ peuplées ou
    // absence de PJ confirmée) — jamais sur un échec de fetch transitoire (null), afin d'autoriser
    // un retry ultérieur. L'ajout est centralisé dans getMailFullBodyContentWithAttachments. Cf. #1332.
    public readonly _attachmentsFetchedSet = new Set<string>();

    /**
     * Record dédié des previews de pièces jointes déjà récupérées (previews lourdes → une seule requête).
     * Clé = `${internetMessageId}::${attachmentId}` (cf. _attachmentPreviewKey) — identité STABLE, insensible
     * au move (le graphId périme). Peuplé UNIQUEMENT au succès du fetch ; purge complète au cleanup().
     * Même esprit que le modèle FetchMissingIfAny (diff présence → fetch manquant → sert depuis le Record).
     *
     * ⚠️ Croissance non bornée assumée : les previews (data URL base64) ne sont évincées qu'à la purge de
     * copie (_forgetAttachmentsFetched) ou au cleanup(), comme les autres Records du manager. Un plafond/LRU
     * (éviction des previews les plus anciennes) POURRAIT être ajouté ici SI un benchmark de consommation
     * mémoire de l'app montre un réel besoin d'optimisation — non implémenté par défaut (YAGNI).
     */
    public attachmentPreviewsRecordByKey: Record<string, string> = {};

    /**
     * Record UNIFIÉ du contenu « widgets » par mail (attachments/links/events), keyé internetMessageId
     * (même espace de clés que mailsRecordByInternetMessageId). Alimente l'affichage docs/links/events
     * d'une CustomView indépendamment de la pagination front. Peuplé au succès du fetch — une entrée vide
     * (`{[],[],[]}`) = mail fetché sans contenu, donc NON redemandé. Purge complète au cleanup().
     */
    public mailViewContentRecordByInternetMessageId: Record<
        string,
        MailViewContent
    > = {};

    /** Toutes les copies de tous les messages, à plat. */
    public allCopies(): Mail[] {
        return Object.values(this.byInternetMessageId).flatMap(
            (copiesByFolder) => [...copiesByFolder.values()],
        );
    }

    /**
     * Les participants des mails chargés (expéditeur, destinataires, copies) : adresse en minuscules et
     * nom affiché, une adresse une seule fois (le premier nom non vide). Pour chercher un nom par
     * l'assistant (`ContactManager.resolveBestContact`), qui applique sa propre règle de nom.
     */
    public people(): { email: string; displayName: string }[] {
        const people = new Map<
            string,
            { email: string; displayName: string }
        >();
        this.allCopies().forEach((mail) => {
            [mail.from, ...mail.to, ...mail.cc, ...mail.bcc].forEach(
                (person) => {
                    const email = person?.email?.trim().toLowerCase();
                    if (!email) return;
                    const displayName = person.name?.trim() ?? "";
                    const known = people.get(email);
                    if (!known) people.set(email, { email, displayName });
                    else if (!known.displayName && displayName)
                        known.displayName = displayName;
                },
            );
        });
        return [...people.values()];
    }

    public searchEmailsByString(searchString: string): string[] {
        const normalizedSearch = normalizeString(searchString);
        // Split search string into individual words (handles multi-word searches like "jean marc")
        const searchWords = normalizedSearch
            .split(/\s+/)
            .filter((w) => w.length > 0);
        const emailsSet = new Set<string>();

        this.allCopies().forEach((mail) => {
            const checkAndAdd = (emailObj: { email: string }) => {
                const normalizedEmail = normalizeString(emailObj.email);

                // Every word must match as a whole word in the email
                const allWordsMatch = searchWords.every((word) => {
                    // Escape special regex characters in the search word
                    const escapedWord = word.replace(
                        /[.*+?^${}()|[\]\\]/g,
                        "\\$&",
                    );
                    // Match word boundaries: start/end of string or common email separators (@._-)
                    // This prevents "eric" from matching "frederic" but allows "jean-marc" when searching "jean"
                    const wordRegex = new RegExp(
                        `(^|[\\s@._-])${escapedWord}($|[\\s@._-])`,
                        "i",
                    );
                    return wordRegex.test(normalizedEmail);
                });

                if (allWordsMatch) {
                    emailsSet.add(emailObj.email);
                }
            };

            checkAndAdd(mail.from);
            mail.to.forEach(checkAndAdd);
            mail.cc.forEach(checkAndAdd);
            mail.bcc.forEach(checkAndAdd);
        });

        return Array.from(emailsSet);
    }
}
